import { clampIndex, reorderItems } from '../Sortable/sortMath';

/** One cell of `NxPanelGrid`: a stable id and its width in grid columns. */
export interface NxPanelGridItem {
  id: string;
  /** Width in columns, `minWidth`..`columns` of the grid. */
  width: number;
}

/** A cell's box in viewport coordinates (a `DOMRect` fits). */
export type NxPanelGridRect = { left: number; right: number; top: number; bottom: number };

/**
 * A width forced into `minWidth..columns`, rounded to whole columns. A
 * `minWidth` above `columns` gives way to `columns`; a width that is no
 * number at all falls back to the full row.
 */
export function clampPanelWidth(width: number, minWidth: number, columns: number): number {
  const max = Math.max(1, Math.floor(columns));
  const min = Math.min(max, Math.max(1, Math.floor(minWidth)));
  if (!Number.isFinite(width)) return max;
  return Math.min(max, Math.max(min, Math.round(width)));
}

/**
 * Distance from one column's start to the next in px: a column plus one gap.
 * `width` is the grid's content width, which holds `columns` columns and
 * `columns - 1` gaps.
 */
export function panelGridPitch(width: number, columns: number, gap: number): number {
  if (!(width > 0) || !(columns > 0)) return 0;
  return (width + gap) / columns;
}

/**
 * The width after dragging the corner `dx` px: whole columns, the nearest
 * one, clamped. Without a measured `pitch` (0) each move counts as one column
 * in its direction.
 */
export function panelWidthFromDelta(
  startWidth: number,
  dx: number,
  pitch: number,
  minWidth: number,
  columns: number
): number {
  const steps = pitch > 0 ? Math.round(dx / pitch) : Math.sign(dx);
  return clampPanelWidth(startWidth + steps, minWidth, columns);
}

/**
 * Where among all cells a drop at (`x`, `y`) inserts: 0 is before the first,
 * `rects.length` after the last. The row is the cells the pointer is
 * vertically inside of (the nearest row while it is in a gap or beyond the
 * grid); inside that row the slot is before the first cell whose middle lies
 * right of the pointer, else after the row's last cell. `rects` are in item
 * order.
 */
export function panelGridDropSlot(rects: readonly NxPanelGridRect[], x: number, y: number): number {
  if (rects.length === 0) return 0;
  const distance = (r: NxPanelGridRect) =>
    y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
  const nearest = Math.min(...rects.map(distance));
  const row: number[] = [];
  rects.forEach((r, index) => {
    if (distance(r) === nearest) row.push(index);
  });
  // Halfway between two rows both tie; the cells of a grid row share their
  // top, so keep the first hit's row only.
  const top = (rects[row[0] as number] as NxPanelGridRect).top;
  const run = row.filter((index) => (rects[index] as NxPanelGridRect).top === top);
  for (const index of run) {
    const r = rects[index] as NxPanelGridRect;
    if (x < (r.left + r.right) / 2) return index;
  }
  return (run[run.length - 1] as number) + 1;
}

/**
 * The index the dragged cell at `from` ends up at for a drop at (`x`, `y`) —
 * the slot with the cell itself taken out, ready for `reorderItems`.
 */
export function panelGridTargetIndex(
  rects: readonly NxPanelGridRect[],
  from: number,
  x: number,
  y: number
): number {
  const slot = panelGridDropSlot(rects, x, y);
  return clampIndex(slot > from ? slot - 1 : slot, rects.length);
}

/** The item at `from` moved to index `to`; a new array, the input stays as it is. */
export function movePanelGridItem<T extends NxPanelGridItem>(
  items: readonly T[],
  from: number,
  to: number
): T[] {
  return reorderItems(items, from, clampIndex(to, items.length));
}

/**
 * The item at `index` with a new (clamped) width; a new array and a new item,
 * the input stays as it is. `null` when nothing changes.
 */
export function resizePanelGridItem<T extends NxPanelGridItem>(
  items: readonly T[],
  index: number,
  width: number,
  minWidth: number,
  columns: number
): T[] | null {
  const item = items[index];
  if (!item) return null;
  const next = clampPanelWidth(width, minWidth, columns);
  if (next === item.width) return null;
  return items.map((it, i) => (i === index ? { ...it, width: next } : it));
}
